<template>
  <div class="my-call-summary-page py-3">
    <header class="mcs-toolbar">
      <div class="mcs-toolbar__title">
        <span class="mcs-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>My Call Summary</h1>
          <p>Calls you placed in the selected date range</p>
        </div>
      </div>
      <div class="mcs-toolbar__actions">
        <CButton size="sm" class="refresh-btn" :class="{ 'is-loading': isLoading }" :disabled="isLoading" @click="loadCalls()">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="mcs-alert">{{ errorMessage }}</CAlert>

    <section class="mcs-section">
      <header class="mcs-section__head">
        <h2>Filters</h2>
        <small class="mcs-section__hint">{{ totalLabel }}</small>
      </header>
      <div class="mcs-section__body">
        <div class="mcs-filters">
          <div class="mcs-field mcs-field--range">
            <label class="mcs-label">Date Range</label>
            <DateRangePicker v-model="dateRange" variant="form" :presets="false" />
          </div>
          <div class="mcs-field">
            <label class="mcs-label">Call Status</label>
            <AppDropdown v-model="filters.status" :options="statusOptions" placeholder="Any Status" />
          </div>
          <div class="mcs-field">
            <label class="mcs-label">Call Type</label>
            <AppDropdown v-model="filters.call_type" :options="callTypeOptions" placeholder="Any Type" />
          </div>
          <div class="mcs-field">
            <label class="mcs-label">Conversion</label>
            <AppDropdown v-model="filters.conversion" :options="conversionOptions" />
          </div>
          <div class="mcs-field mcs-field--search">
            <label class="mcs-label">Search</label>
            <div class="mcs-search-row">
              <input v-model.trim="filters.search" type="text" class="mcs-input" placeholder="Name or phone…" @keydown.enter="applyFilters">
              <CButton :disabled="isLoading" class="mcs-apply-btn" @click="applyFilters">{{ isLoading ? '…' : 'Apply' }}</CButton>
              <CButton :disabled="isLoading" class="mcs-reset-btn" @click="resetFilters">Reset</CButton>
            </div>
          </div>
        </div>
      </div>
    </section>

    <BaseTable
      title="My Calls"
      :items="tableRows"
      :columns="columns"
      :loading="isLoading"
      :sortable="false"
      :show-count="false"
      empty-text="No calls placed by you for the current filters."
    >
      <template #toolbar>
        <span class="mcs-table-hint">Showing {{ pageRangeLabel }}</span>
      </template>

      <template #cell-status="{ row }">
        <span :class="['mcs-pill', row.status_class]">{{ row.status_label || '—' }}</span>
      </template>

      <template #cell-converted="{ row }">
        <span v-if="row.converted_order" class="mcs-converted">{{ row.converted_order }}</span>
        <span v-else class="mcs-converted mcs-converted--no">—</span>
      </template>

      <template #cell-comments="{ value }">
        <span class="mcs-comments" :title="value || ''">{{ value || '—' }}</span>
      </template>

      <template #cell-actions="{ row }">
        <div class="mcs-row-actions">
          <button
            type="button"
            class="mcs-history-btn"
            :disabled="!row.phone"
            @click="openHistory(row)"
          >
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M3 3v5h5" />
              <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
              <path d="M12 7v5l4 2" />
            </svg>
            <span>View history</span>
          </button>
          <button
            type="button"
            class="mcs-call-btn"
            :disabled="!row.phone"
            @click="callContact(row)"
          >
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
            </svg>
            <span>Call</span>
          </button>
        </div>
      </template>
    </BaseTable>

    <CustomerHistoryModal :show.sync="historyModalShow" :phone="historyPhone" :name="historyName" />

    <div v-if="!isLoading && calls.length && lastPage > 1" class="mcs-pagination-bar">
      <span class="mcs-pagination-info">{{ pageRangeLabel }}</span>
      <AppPager :current-page="currentPage" :last-page="lastPage" @change="goToPage" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import DateRangePicker from '@/components/DateRangePicker.vue'
import BaseTable from '@/components/BaseTable.vue'
import AppPager from '@/components/AppPager.vue'
import CustomerHistoryModal from '@/components/CustomerHistoryModal.vue'

function getCurrentUserId () {
  const raw = localStorage.getItem('auth_user')
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    const id = Number.parseInt(parsed && parsed.id, 10)
    return Number.isInteger(id) ? id : null
  } catch (error) {
    return null
  }
}

export default {
  name: 'MyCallSummary',
  components: { AppDropdown, DateRangePicker, BaseTable, AppPager, CustomerHistoryModal },
  data () {
    const today = new Date().toISOString().slice(0, 10)
    const thirtyDaysAgo = new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    return {
      isLoading: false,
      errorMessage: '',
      calls: [],
      total: 0,
      currentPage: 1,
      lastPage: 1,
      perPage: 25,
      currentUserId: getCurrentUserId(),
      historyModalShow: false,
      historyPhone: '',
      historyName: '',
      statusOptions: [],
      callTypeOptions: [],
      conversionOptions: [
        { value: '', label: 'All Calls' },
        { value: 'converted', label: 'Converted to Sale' },
        { value: 'not_converted', label: 'Call Log Only' }
      ],
      filters: {
        fromDate: thirtyDaysAgo,
        toDate: today,
        status: '',
        call_type: '',
        conversion: '',
        search: ''
      },
      columns: [
        { key: 'date_time', label: 'Date / Time' },
        { key: 'contact', label: 'Contact' },
        { key: 'phone', label: 'Phone' },
        { key: 'call_type', label: 'Type' },
        { key: 'status', label: 'Status' },
        { key: 'duration', label: 'Duration', align: 'right' },
        { key: 'converted', label: 'Converted' },
        { key: 'comments', label: 'Comments', width: '280px' },
        { key: 'actions', label: 'Actions', align: 'right' }
      ]
    }
  },
  computed: {
    totalLabel () {
      return this.total ? `${this.total} total` : ''
    },
    pageRangeLabel () {
      if (!this.calls.length) return '0'
      const start = (this.currentPage - 1) * this.perPage + 1
      const end = start + this.calls.length - 1
      return `${start}–${end} of ${this.total}`
    },
    dateRange: {
      get () {
        return { fromDate: this.filters.fromDate, toDate: this.filters.toDate }
      },
      set (value) {
        this.filters.fromDate = (value && value.fromDate) || ''
        this.filters.toDate = (value && value.toDate) || ''
      }
    },
    tableRows () {
      return this.calls.map((call) => ({
        id: call.id,
        date_time: this.formatDateTime(call.created_at),
        contact: (call.contact && call.contact.name) || '',
        phone: (call.contact && call.contact.phone) || '',
        call_type: this.formatLabel(call.call_type),
        status: this.formatLabel(call.status),
        status_label: this.formatLabel(call.status),
        status_class: this.statusClass(call.status),
        duration: this.formatDuration(call.duration),
        converted: (call.order && call.order.order_number) || '',
        converted_order: (call.order && call.order.order_number) || '',
        comments: call.comments || ''
      }))
    }
  },
  created () {
    this.loadBootstrapData()
    this.loadCalls()
  },
  methods: {
    formatLabel (value) {
      return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    formatDateTime (timestamp) {
      if (!timestamp) return ''
      const ms = typeof timestamp === 'number' ? timestamp * 1000 : Date.parse(timestamp)
      if (!Number.isFinite(ms)) return ''
      const d = new Date(ms)
      const pad = (n) => String(n).padStart(2, '0')
      return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    formatDuration (seconds) {
      const total = Math.max(0, Number(seconds || 0))
      if (!total) return '—'
      const minutes = Math.floor(total / 60)
      const remainder = total % 60
      return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`
    },
    statusClass (status) {
      const s = String(status || '').toLowerCase()
      if (s.includes('sold') || s === 'closed') return 'is-success'
      if (s.includes('not interested')) return 'is-muted'
      if (s.includes('not attended')) return 'is-warn'
      if (s.includes('feedback') || s.includes('follow') || s.includes('call_again')) return 'is-info'
      return 'is-default'
    },
    buildDropdown (entries) {
      return (entries || []).map((entry) => ({
        value: entry.slug || entry.value || entry.id || '',
        label: entry.name || entry.text || entry.slug || ''
      })).filter((entry) => entry.value || entry.label)
    },
    loadBootstrapData () {
      axios.get(`${this.$apiAdress}/api/order_form_data`)
        .then((response) => {
          const data = response.data || {}
          const dropdown = data.drop_down_items || {}
          this.callTypeOptions = [{ value: '', label: 'Any Type' }, ...this.buildDropdown(dropdown.call_type)]
          this.statusOptions = [{ value: '', label: 'Any Status' }, ...this.buildDropdown(dropdown.call_status)]
        })
        .catch((error) => {
          console.log(error)
        })
    },
    loadCalls (page = this.currentPage) {
      this.errorMessage = ''
      // Guard: if we can't identify the caller, surface a clear error
      // instead of silently dropping the user_id and showing every call
      // in the organisation.
      if (!this.currentUserId) {
        this.errorMessage = 'Unable to identify the current user. Please sign in again.'
        this.calls = []
        return
      }
      this.isLoading = true
      const payload = {
        page,
        per_page: this.perPage,
        user_id: this.currentUserId,
        fromDate: this.filters.fromDate || undefined,
        toDate: this.filters.toDate || undefined,
        status: this.filters.status || undefined,
        call_type: this.filters.call_type || undefined,
        conversion: this.filters.conversion || undefined,
        search: this.filters.search || undefined
      }
      axios.post(`${this.$apiAdress}/api/calls/full_listing`, payload)
        .then((response) => {
          const data = response.data || {}
          this.calls = Array.isArray(data.data) ? data.data : []
          this.currentPage = Number(data.current_page || 1)
          this.lastPage = Number(data.last_page || 1)
          this.total = Number(data.total || 0)
          this.perPage = Number(data.per_page || this.perPage)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load your call summary.'
          this.calls = []
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    applyFilters () {
      this.currentPage = 1
      this.loadCalls(1)
    },
    resetFilters () {
      const today = new Date().toISOString().slice(0, 10)
      const thirtyDaysAgo = new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
      this.filters = { fromDate: thirtyDaysAgo, toDate: today, status: '', call_type: '', conversion: '', search: '' }
      this.currentPage = 1
      this.loadCalls(1)
    },
    goToPage (page) {
      if (page < 1 || page > this.lastPage) return
      this.loadCalls(page)
    },
    callContact (row) {
      if (!row || !row.phone) return
      this.$router.push({ path: '/telesales', query: { phone: row.phone } })
    },
    openHistory (row) {
      if (!row || !row.phone) return
      this.historyPhone = row.phone
      this.historyName = row.contact || ''
      this.historyModalShow = true
    }
  }
}
</script>

<style scoped>
.my-call-summary-page {
  padding: 0.25rem 0;
}

.mcs-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.mcs-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.mcs-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.mcs-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.mcs-toolbar__accent {
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.mcs-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.mcs-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.mcs-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.mcs-toolbar__actions >>> .btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.mcs-toolbar__actions >>> .btn.refresh-btn:focus { outline: none; }
.mcs-toolbar__actions >>> .btn.refresh-btn:focus-visible {
  outline: none;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.mcs-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.mcs-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: mcs-spin 0.9s linear infinite;
}

@keyframes mcs-spin {
  to { transform: rotate(360deg); }
}

.mcs-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.mcs-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.mcs-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.mcs-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.mcs-section__hint {
  color: #6b7280;
  font-size: 0.78rem;
}

.mcs-section__body {
  padding: 1rem;
}

.mcs-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.mcs-input {
  width: 100%;
  height: 36px;
  padding: 0.3rem 0.6rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #2f3640;
  background: #ffffff;
}

.mcs-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.mcs-filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.85rem;
}

.mcs-field {
  display: flex;
  flex-direction: column;
}

.mcs-field--range {
  grid-column: span 2;
  min-width: 280px;
}

.mcs-field--search {
  grid-column: 1 / -1;
}

.mcs-search-row {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.mcs-search-row .mcs-input {
  flex: 1 1 220px;
  min-width: 200px;
}

.mcs-search-row >>> .btn {
  height: 36px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0 1rem;
}

.mcs-search-row >>> .btn.mcs-apply-btn {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.mcs-search-row >>> .btn.mcs-apply-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.mcs-search-row >>> .btn.mcs-apply-btn:disabled {
  opacity: 0.75;
  cursor: progress;
}

.mcs-search-row >>> .btn.mcs-apply-btn:focus { outline: none; }
.mcs-search-row >>> .btn.mcs-apply-btn:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.mcs-search-row >>> .btn.mcs-reset-btn {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #4f5560;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.mcs-search-row >>> .btn.mcs-reset-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.mcs-search-row >>> .btn.mcs-reset-btn:focus { outline: none; }
.mcs-search-row >>> .btn.mcs-reset-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.mcs-table-hint {
  font-size: 0.78rem;
  color: #6b7280;
}

.mcs-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
}

.mcs-pill.is-success {
  background: #dff5ea;
  color: #0e5b46;
}

.mcs-pill.is-info {
  background: #e0edff;
  color: #1e40af;
}

.mcs-pill.is-warn {
  background: #fef3c7;
  color: #92400e;
}

.mcs-pill.is-muted {
  background: #f1f5f9;
  color: #64748b;
}

.mcs-pill.is-default {
  background: #eef1f7;
  color: #4b5563;
}

.mcs-converted {
  font-weight: 600;
  color: #0e5b46;
}

.mcs-converted--no {
  color: #9ca3af;
  font-weight: 500;
}

.mcs-comments {
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.mcs-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.85rem;
}

.mcs-pagination-info {
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
}

.mcs-row-actions {
  display: inline-flex;
  gap: 0.4rem;
  justify-content: flex-end;
  white-space: nowrap;
}

.mcs-history-btn,
.mcs-call-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 28px;
  padding: 0 0.7rem;
  border-radius: 6px;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, filter 0.15s ease, transform 0.15s ease;
}

.mcs-history-btn {
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
}

.mcs-history-btn svg {
  color: #16a07a;
  flex-shrink: 0;
}

.mcs-history-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.mcs-call-btn {
  border: 0;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 5px rgba(14, 91, 70, 0.18);
}

.mcs-call-btn svg {
  flex-shrink: 0;
}

.mcs-call-btn:hover:not(:disabled) {
  filter: brightness(1.05);
  transform: translateY(-1px);
}

.mcs-history-btn:disabled,
.mcs-call-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mcs-history-btn:focus,
.mcs-call-btn:focus { outline: none; }

.mcs-history-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.mcs-call-btn:focus-visible {
  outline: none;
  box-shadow: 0 2px 5px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.22);
}
</style>
